// --- Core ---
import {get} from "local-storage";
import {find} from "underscore";
// --- SaniQ ---
import {fetchAPI} from "api";
//Flags
import deFlag from "images/flags/DE.png";
import gbFlag from "images/flags/GB.png";
import frFlag from "images/flags/FR.png";
import elFlag from "images/flags/EL.png";
import itFlag from "images/flags/IT.png";
import ptFlag from "images/flags/PT.png";
import esFlag from "images/flags/ES.png";
import ruFlag from "images/flags/RU.png";
import fiFlag from "images/flags/FI.png";
import nlFlag from "images/flags/NL.png";

//NOTE(Keune): Make sure any Locale specified in the enum also has an entry in the localeDefinitions
enum Locale {
  DE = 'de',
  EN = 'en',
  NL = 'nl',
  IT = 'it',
  FR = 'fr',
  RU = 'ru',
  FI = 'fi',
  EL = 'el',
  ES = 'es',
  PT = 'pt'
}

const DEFAULT_LOCALE = Locale.EN;

interface LocaleDefinition {
  key: Locale;
  flag: string;
  name: string;
  secondary?: string;
}

/* eslint-disable formatjs/no-literal-string-in-object */
// Definitions for supported locales by the SaniQ web portal. Includes manually and automatically translated locales
const localeDefinitions: LocaleDefinition[] = [
  {key: Locale.DE, flag: deFlag, name: "Deutsch"},
  {key: Locale.EN, flag: gbFlag, name: "English"},
  {key: Locale.NL, flag: nlFlag, name: 'Nederlands'},
  {key: Locale.FR, flag: frFlag, name: "Français", secondary: "Cette langue vous est fournie en tant que traduction automatique par DeepL."},
  {key: Locale.EL, flag: elFlag, name: "Ελληνική", secondary: "Αυτή η γλώσσα σας παρέχεται ως αυτόματη μετάφραση από το DeepL."},
  {key: Locale.IT, flag: itFlag, name: "Italiano", secondary: "Questa lingua ti viene fornita come traduzione automatica da DeepL."},
  {key: Locale.PT, flag: ptFlag, name: "Português", secondary: "Este idioma se ofrece como una traducción automática de DeepL."},
  {key: Locale.ES, flag: esFlag, name: "Español", secondary: "Esta língua é-lhe fornecida como uma tradução automática a partir de DeepL."},
  {key: Locale.RU, flag: ruFlag, name: "Русский", secondary: "Этот язык предоставлен вам в качестве автоматического перевода сайтом DeepL."},
  {key: Locale.FI, flag: fiFlag, name: "Suomi", secondary: "Tämä kieli tarjotaan sinulle automaattisena käännöksenä osoitteessa DeepL."}
];
/* eslint-enable formatjs/no-literal-string-in-object */

const safeLocale = (unsafeLocale: string): Locale => {
  const [localeWithoutRegionCode] = unsafeLocale.toLowerCase().split(/[_-]+/);

  return find(Locale, e => e === localeWithoutRegionCode) || DEFAULT_LOCALE;
};

//NOTE(Keune): Special typing is necessary, since the languages endpoint still uses the old serializer. In this case fetchAPI does not return a
// DeserializedRecord
interface LanguagesResponse {
  is_language_set: boolean,
  language: string
}

// Get the locale (without region code) for i18n
//
// 1. Select stored locale when previously visited (local storage)
// 2. Select tenant's default locale
// 3. When not a tenant, select user's browser locale
// 4. Fallback to default locale
function language(): Promise<Locale> {
  // Define user's language. Different browsers have the user locale defined
  // on different fields on the `navigator` object, so we make sure to account
  // for these different by checking all of them
  const locale = get<string>('locale');

  if (locale) {
    return Promise.resolve(safeLocale(locale));
  }
  const fallbackLocale = navigator.languages?.[0] || navigator.language || Locale.EN;

  return fetchAPI<LanguagesResponse>('/web/v1/tenants/languages', 'POST')
    .then(({content}) => {
      if (content.is_language_set) {
        return content.language;
      } else {
        return fallbackLocale;
      }
    })
    .catch(() => fallbackLocale)
    .then(safeLocale);
}

function messages(locale: Locale) {
  switch (locale) {
    case Locale.DE:
      return import('../../lang/compiled/de.json');
    case Locale.FR:
      return import('../../lang/compiled/fr.json');
    case Locale.EL:
      return import('../../lang/compiled/el.json');
    case Locale.ES:
      return import('../../lang/compiled/es.json');
    case Locale.PT:
      return import('../../lang/compiled/pt.json');
    case Locale.IT:
      return import('../../lang/compiled/it.json');
    case Locale.RU:
      return import('../../lang/compiled/ru.json');
    case Locale.FI:
      return import('../../lang/compiled/fi.json');
    case Locale.NL:
      return import('../../lang/compiled/nl.json');
    default:
      return import('../../lang/compiled/en.json');
  }
}

export {
  DEFAULT_LOCALE,
  messages,
  language,
  Locale,
  LocaleDefinition,
  localeDefinitions,
  safeLocale
};
